// Compliance rollup dashboard (C4.5) — the auditor/admin analytical surface. Reads the
// v_verwerking_compliance_status MV via the admin-worker (super_tenant_admin, bypass + explicit
// tenantId filter) and renders the full/partial/gap summary + per-verwerking rows. PLAN-C4.5.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type Rollup = { rows: Array<Record<string, unknown>>; summary: { full: number; partial: number; gap: number; total: number } };
type RollupRow = Record<string, unknown>;
const CLASS_COLOR: Record<string, string> = { full: "text-emerald-700", partial: "text-amber-700", gap: "text-destructive" };

export function ComplianceRoute() {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState("");
  const [submitted, setSubmitted] = useState("");

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["admin", "compliance", submitted],
    queryFn: () => api.getComplianceRollup(submitted) as Promise<Rollup>,
    enabled: submitted.length > 0,
  });
  const refresh = useMutation({
    mutationFn: () => api.refreshComplianceRollup(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "compliance"] }),
  });

  const summary = data?.summary;
  const rows = data?.rows ?? [];

  const columns: Column<RollupRow>[] = [
    { key: "verwerkingId", header: "Verwerking", render: (r) => `${String(r.verwerkingId ?? "—").slice(0, 8)}…` },
    { key: "istMaturityScore", header: "Maturity", render: (r) => String(r.istMaturityScore ?? "—") },
    { key: "weightedScore", header: "Weighted", render: (r) => String(r.weightedScore ?? "—") },
    {
      key: "complianceClassification", header: "Classification",
      render: (r) => (
        <span className={cn("font-medium", CLASS_COLOR[String(r.complianceClassification)] ?? "text-foreground")}>
          {String(r.complianceClassification ?? "—")}
        </span>
      ),
    },
  ];

  return (
    <section>
      <PageHeader
        title="Compliance rollup"
        description="Per-verwerking maturity classification (full / partial / gap) for a tenant, from the analytical MV."
        actions={isFetching ? <span className="text-sm text-muted-foreground">updating…</span> : undefined}
      />

      <form onSubmit={(e) => { e.preventDefault(); setSubmitted(tenantId.trim()); }} className="mb-4 flex flex-wrap items-center gap-2">
        <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="tenant id (uuid)" className="w-[360px] max-w-full" />
        <Button type="submit" disabled={!tenantId.trim()}>Load</Button>
        <Button type="button" variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          {refresh.isPending ? "Refreshing…" : "Refresh MV"}
        </Button>
      </form>

      {refresh.isError && <p className="text-sm text-destructive">{(refresh.error as Error).message}</p>}
      {!submitted && <p className="text-sm text-muted-foreground">Enter a tenant id to load its compliance rollup.</p>}
      {error && <p className="text-sm text-destructive">Failed: {(error as Error).message}</p>}

      {submitted && summary && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="Full" value={String(summary.full)} valueClass="text-emerald-700" />
            <Tile label="Partial" value={String(summary.partial)} valueClass="text-amber-700" />
            <Tile label="Gap" value={String(summary.gap)} valueClass="text-destructive" />
            <Tile label="Total verwerkingen" value={String(summary.total)} />
          </div>
          <DataTable
            columns={columns}
            rows={rows}
            loading={isLoading}
            getRowId={(r) => String(r.verwerkingId ?? Math.random())}
            empty="No maturity data for this tenant."
          />
        </>
      )}
    </section>
  );
}

function Tile({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className={cn("text-2xl", valueClass)}>{value}</CardTitle>
      </CardHeader>
      <CardContent />
    </Card>
  );
}
